// /dtv/$category — TH detail page
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { DTV_CATEGORIES, type DtvCategory } from "@/data/dtv-categories";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, howToSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { NationalityGridLinks } from "@/components/NationalityGridLinks";
import { Phone, MessageCircle, ArrowRight, Sparkles, FileText, Wallet, Clock, Users, ChevronRight, ShieldCheck } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/dtv/$category/")({
  loader: async ({ params }) => {
    const { DTV_CATEGORIES: LIST } = await import("@/data/dtv-categories");
    const c = LIST.find((x) => x.slug === params.category);
    if (!c) throw notFound();
    return { category: c };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const c = loaderData.category;
    const url = `${SITE}/dtv/${c.slug}`;
    const title = `${c.titleTh} — ขั้นตอน เอกสาร ค่าธรรมเนียม 2026 | NYC`;
    const desc = c.quickAnswerTh.slice(0, 155);
    return {
      meta: [
        { title: ctaTitle(title, "/dtv/$category", "th") }, { name: "description", content: ctaDesc(desc, "/dtv/$category", "th")},
        { property: "og:title", content: ctaTitle(title, "/dtv/$category", "th")}, { property: "og:description", content: ctaDesc(desc, "/dtv/$category", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/dtv/${c.slug}`, `/en/dtv/${c.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: c.titleTh, serviceType: c.titleEn, description: desc,
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: "TH",
              offers: { "@type": "Offer", price: String(c.servicePriceTHB), priceCurrency: "THB", description: `บริการยื่น ${c.titleTh}` },
            }),
          },
          howToSchema({
            name: `วิธีขอ ${c.titleTh}`,
            description: c.taglineTh,
            totalTime: "P15D",
            estimatedCost: { currency: "THB", value: String(c.servicePriceTHB + 10000) },
            inLanguage: "th",
            steps: c.stepsTh,
          }),
          faqPageSchema(c.faqsTh),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "DTV", item: `${SITE}/dtv` },
                { "@type": "ListItem", position: 3, name: c.titleTh, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-3xl font-extrabold">ไม่พบหมวด DTV นี้</h1>
      <Link to="/dtv" className="mt-6 inline-block rounded-lg bg-primary px-5 py-3 font-bold text-primary-foreground">กลับไปหน้า DTV</Link>
    </main>
  ),
  errorComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-2xl font-bold">เกิดข้อผิดพลาด</h1>
      <Link to="/dtv" className="mt-4 inline-block text-primary">กลับไปหน้า DTV</Link>
    </main>
  ),
  component: DtvCategoryTh,
});

function DtvCategoryTh() {
  const { category: c } = Route.useLoaderData() as { category: DtvCategory };
  const others = DTV_CATEGORIES.filter((x) => x.slug !== c.slug).slice(0, 6);
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-5xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link>
            <ChevronRight className="mx-1 inline h-3 w-3" />
            <Link to="/dtv" className="hover:text-primary">DTV</Link>
            <ChevronRight className="mx-1 inline h-3 w-3" />
            <span>{c.titleTh}</span>
          </nav>
          <div className="text-4xl">{c.icon}</div>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">{c.titleTh}</h1>
          <p className="mt-3 max-w-3xl text-lg text-muted-foreground">{c.taglineTh}</p>

          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <p className="text-sm leading-relaxed text-foreground sm:text-base">{c.quickAnswerTh}</p>
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-3 max-w-3xl">
            <div className="rounded-xl border border-border bg-card p-3">
              <Wallet className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">ค่าธรรมเนียมราชการ</div>
              <div className="text-sm font-bold">10,000 บาท</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-3">
              <Clock className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">เวลาพิจารณา</div>
              <div className="text-sm font-bold">{c.processingDays}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-3">
              <Users className="h-4 w-4 text-primary" />
              <div className="mt-1 text-xs text-muted-foreground">Sponsor</div>
              <div className="text-sm font-bold line-clamp-2">{c.sponsorTh}</div>
            </div>
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> โทร 092-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h2 className="text-2xl font-extrabold">ขั้นตอนการยื่น ({c.stepsTh.length} step)</h2>
              <ol className="mt-4 space-y-3">
                {c.stepsTh.map((s, i) => (
                  <li key={i} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">{i + 1}</div>
                      <div>
                        <div className="font-bold">{s.name}</div>
                        <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold">เอกสารที่ต้องเตรียม</h2>
              <ul className="mt-4 space-y-2">
                {c.docsTh.map((d, i) => (
                  <li key={i} className="flex items-start gap-2 rounded-lg border border-border bg-card p-3 text-sm">
                    <FileText className="h-4 w-4 shrink-0 text-primary" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold">คำถามที่พบบ่อย</h2>
              <div className="mt-4 space-y-3">
                {c.faqsTh.map((f, i) => (
                  <details key={i} className="rounded-xl border border-border bg-card p-4">
                    <summary className="cursor-pointer font-bold">{f.q}</summary>
                    <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
                  </details>
                ))}
              </div>
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-bold">ใครยื่นได้?</h3>
              <p className="mt-2 text-sm text-muted-foreground">{c.whoTh}</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-bold">Sponsor ที่ต้องใช้</h3>
              <p className="mt-2 text-sm text-muted-foreground">{c.sponsorTh}</p>
            </div>
            <div className="rounded-2xl border border-primary bg-primary/5 p-5">
              <h3 className="font-bold">บริการของ NYC</h3>
              <p className="mt-2 text-2xl font-extrabold text-primary">฿{c.servicePriceTHB.toLocaleString()}</p>
              <p className="mt-1 text-xs text-muted-foreground">ไม่รวมค่าธรรมเนียมราชการ 10,000 บาท — รวม document prep + financial structuring + ยื่น e-Visa</p>
              <a href="tel:+66922429293" className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-bold text-primary-foreground">
                <Phone className="h-4 w-4" /> โทรปรึกษา
              </a>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <h3 className="font-bold">บริการเสริม</h3>
              <ul className="mt-2 space-y-1 text-sm">
                <li><Link to="/translate" className="text-primary hover:underline">แปลเอกสาร sponsor</Link></li>
                <li><Link to="/apostille" className="text-primary hover:underline">Apostille / Legalisation</Link></li>
                <li><Link to="/visa-compliance" className="text-primary hover:underline">TM30 + 90-day report</Link></li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-xl font-extrabold"><ShieldCheck className="mr-2 inline h-5 w-5 text-primary" />DTV ประเภทอื่น</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((o) => (
              <Link key={o.slug} to="/dtv/$category" params={{ category: o.slug }} className="rounded-xl border border-border bg-card p-3 hover:border-primary">
                <div className="flex items-center gap-2"><span className="text-xl">{o.icon}</span><span className="font-bold text-sm">{o.titleTh}</span></div>
                <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">{o.taglineTh}</div>
                <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary">ดู <ArrowRight className="h-3 w-3" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <NationalityGridLinks lang="th" hrefFor={(n) => `/dtv/${c.slug}/for/${n}`} />
      <TrustLayer variant="compact" />
    </main>
  );
}
